import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import LaneTable from './components/LaneTable';
import DirectQuotesTable from './components/DirectQuotesTable';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import QuoteLaneEditorDrawer from './components/QuoteLaneEditorDrawer';
import CustomerInfoTab from './tabs/CustomerInfoTab';
import quoteService from '../../services/quote.service';
import type { QuoteCreatePayload, QuoteDetailData, QuoteGroup, QuoteListRow } from './fixtures';

/* ──────────────────────────────────────────────────────────────────────────
 * GroupedQuoteDetailPage (Task 7 + Quotes — Lane Table + Editor Drawer Task C)
 * — a DEDICATED detail page for ONE (customer, bill-to) group. Coexists with
 * the single-quote QuoteDetailPage (which it does NOT modify). Layout, top →
 * bottom:
 *   1. Shared group header (customer + bill-to; optionally enriched via the
 *      first lane's getQuote → reused CustomerInfoTab content).
 *   2. Direct Quotes (N): a <DirectQuotesTable> (shared TanTable) over the group's lanes (all DIRECT now),
 *      + "+ Add Quote". Clicking a lane opens the <QuoteLaneEditorDrawer>
 *      in place. Hidden when there are no lanes.
 *
 * Group data is supplied via router state (primary, no refetch); a deep-link /
 * refresh with no state falls back to searchQuotesGrouped and resolves the
 * group by its route params (customercode + billto disambiguate same-name
 * customers). After the drawer reports a change (save / revision / dupe) we
 * re-run that same resolve to refresh the lane table — router state never
 * updates on its own. ─────────────────────────────────────────────────────── */

/** Resolve a group by its route key against the FULL grouped search.
 *
 * searchQuotesGrouped({ all: true }) loads EVERY group (the load-all mode from
 * LA-1), not just the first ~25-group page, so any (customercode, billToNumber)
 * resolves on a cold deep-link / refresh — regardless of which page it would
 * otherwise land on. The primary path is still router-state navigation from the
 * Quotes grid; this resolve backs both the deep-link / refresh AND the
 * post-change refetch. A miss therefore means the group genuinely isn't in the
 * full set (removed / changed), not a paging artifact. */
async function resolveGroupByRoute(
  wantCode: number | null,
  wantBillTo: number,
): Promise<QuoteGroup | null> {
  const resp = await quoteService.searchQuotesGrouped({ all: true });
  return (
    resp.groups.find(
      (g) => g.customercode === wantCode && g.billToNumber === wantBillTo,
    ) ?? null
  );
}

const GroupedQuoteDetailPage: React.FC = () => {
  const { customercode, billto } = useParams<{ customercode: string; billto: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Route params → the numeric grouping key used to resolve the group on a
  // cold deep-link (no router state) and on a post-change refetch. customercode
  // may be "null" for an un-coalesced customer; treat a non-numeric param as null.
  const codeParam = Number(customercode);
  const wantCode: number | null = Number.isNaN(codeParam) ? null : codeParam;
  const wantBillTo = Number(billto);

  // Primary: the grid hands us the group via router state (no refetch).
  const stateGroup = (location.state as { group?: QuoteGroup } | null)?.group ?? null;

  const [group, setGroup] = React.useState<QuoteGroup | null>(stateGroup);
  const [loading, setLoading] = React.useState(stateGroup == null);
  const [error, setError] = React.useState<string | null>(null);
  const [notFound, setNotFound] = React.useState(false);

  // Fallback fetch (deep-link / refresh): search grouped, then pick the group
  // whose customercode + billToNumber match the route. Only runs when router
  // state didn't supply the group.
  React.useEffect(() => {
    let cancelled = false;
    // Always resolve the FULL (unfiltered) group so the account view shows every
    // quote for the customer/bill-to and the averages span ALL of them — the
    // grid's search filter narrows the LIST of accounts, not an account's own
    // quotes. stateGroup (from the grid) paints instantly; this overwrites it
    // with the complete account + all-quotes averages.
    if (!stateGroup) setLoading(true);
    setError(null);
    setNotFound(false);
    resolveGroupByRoute(wantCode, wantBillTo)
      .then((match) => {
        if (cancelled) return;
        if (match) setGroup(match);
        else if (!stateGroup) setNotFound(true);
      })
      .catch((e: unknown) => {
        if (!cancelled && !stateGroup) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [stateGroup, wantCode, wantBillTo]);

  // Post-change refetch (drawer onChanged): re-resolve the group from the
  // server so the lane table reflects the saved status/total/new revision/new
  // dupe lane. Router state can't update itself; this re-uses the same resolve
  // as the deep-link fallback (now load-all). A miss leaves the existing group
  // in place (the change still happened — we just couldn't pull a fresh snapshot).
  const refetchGroup = React.useCallback(() => {
    resolveGroupByRoute(wantCode, wantBillTo)
      .then((match) => { if (match) setGroup(match); })
      .catch((e: unknown) => {
        console.warn('[GroupedQuoteDetail] refetch after change failed', e);
      });
  }, [wantCode, wantBillTo]);

  const goBack = () => navigate('/order-management/quotes');

  if (loading) {
    return (
      <Shell goBack={goBack} title="Quote group">
        <div className="flex-1 flex items-center justify-center text-slate-500 dark:text-slate-400 text-[0.78125rem]" role="status">
          Loading group…
        </div>
      </Shell>
    );
  }
  if (error) {
    return (
      <Shell goBack={goBack} title="Failed to load group">
        <CenteredCard title="Failed to load group" body={error} />
      </Shell>
    );
  }
  if (notFound || !group) {
    // The fallback now loads ALL groups, so a miss means this group genuinely
    // isn't in the full set — it was likely removed or its customer / bill-to
    // changed. Keep the copy honest + actionable rather than blaming paging.
    return (
      <Shell goBack={goBack} title="Group not found">
        <CenteredCard
          title="Group not found"
          body="We couldn't find this quote group — it may have been removed or changed. Head back to the Quotes list to find it."
          action={{ label: 'Go to Quotes list', onClick: goBack }}
        />
      </Shell>
    );
  }

  return <GroupBody group={group} goBack={goBack} refetchGroup={refetchGroup} />;
};

interface BodyProps {
  group: QuoteGroup;
  goBack: () => void;
  refetchGroup: () => void;
}

const GroupBody: React.FC<BodyProps> = ({ group, goBack, refetchGroup }) => {
  const navigate = useNavigate();
  // The lane whose editor drawer is open (DIRECT only — warehouse lanes route
  // out to the single-quote editor). null = drawer closed.
  const [openLane, setOpenLane] = React.useState<QuoteListRow | null>(null);

  // ── "Ended" header toggle (off by default). The base `group` is resolved
  //    with show_ended=false, so its lanes are active-only — that drives the
  //    header card + averages and stays stable. When the toggle is switched on
  //    we lazily pull the SAME group resolved with show_ended=true (a superset:
  //    the active lanes plus the latest revision of any ended/expired quotes)
  //    and feed only the TABLE from it. Cached after the first fetch; the cache
  //    is invalidated whenever the base group changes (e.g. a drawer save). ──
  const [showEnded, setShowEnded] = React.useState(false);
  const [endedGroup, setEndedGroup] = React.useState<QuoteGroup | null>(null);
  const [endedLoading, setEndedLoading] = React.useState(false);

  // A fresh base group (deep-link resolve or post-save refetch) invalidates any
  // cached with-ended snapshot so the toggle re-pulls current data.
  React.useEffect(() => { setEndedGroup(null); }, [group]);

  React.useEffect(() => {
    if (!showEnded || endedGroup) return;
    let cancelled = false;
    setEndedLoading(true);
    quoteService.searchQuotesGrouped({ all: true, showEnded: true })
      .then((resp) => {
        if (cancelled) return;
        const match = resp.groups.find(
          (g) => g.customercode === group.customercode && g.billToNumber === group.billToNumber,
        ) ?? null;
        setEndedGroup(match);
      })
      .catch((e: unknown) => {
        console.warn('[GroupedQuoteDetail] ended-quotes fetch failed', e);
      })
      .finally(() => { if (!cancelled) setEndedLoading(false); });
    return () => { cancelled = true; };
  }, [showEnded, endedGroup, group.customercode, group.billToNumber]);

  // Direct lanes edit in-place; Warehouse lanes open the Warehouse editor. The section hides
  // when empty. With the toggle on, list the with-ended superset (falling back
  // to the active lanes until that fetch resolves); otherwise active-only.
  const visibleLanes = showEnded ? (endedGroup?.lanes ?? group.lanes) : group.lanes;
  const directLanes = visibleLanes.filter((l) => l.quoteType === 'DIRECT');
  const warehouseLanes = visibleLanes.filter((l) => l.quoteType === 'WAREHOUSE');

  // Close the drawer when the group identity changes.
  React.useEffect(() => {
    setOpenLane(null);
  }, [group.customercode, group.billToNumber]);

  // Both sections open the in-place editor drawer: it edits DIRECT and
  // WAREHOUSE quotes alike (Dray / Warehouse / Delivery tabs for the latter).
  const handleOpenDirect = React.useCallback((lane: QuoteListRow) => {
    setOpenLane(lane);
  }, []);
  const handleOpenWarehouse = React.useCallback((lane: QuoteListRow) => {
    setOpenLane(lane);
  }, []);

  // ── "+ Add Quote": launch the create wizard pre-filled with this group's
  //    customer + bill-to (V1 deferred new-bill-to keeps billto=0 + name). ──
  const [createOpen, setCreateOpen] = React.useState(false);
  const addLaneHeader: Partial<QuoteCreatePayload['header']> = React.useMemo(() => ({
    billto: group.billToNumber,
    billtoname: group.billToNumber === 0 ? '' : group.billToName,
    customercode: group.customercode ?? undefined,
    customername: group.customerName,
    quotetype: 'DIRECT',
  }), [group]);

  const isNewBillTo = group.billToNumber === 0;
  // Only used in the non-new-bill-to branch below (the new-bill-to case renders
  // its own pill), so this is simply the bill-to name with an em-dash fallback.
  const billToLabel = group.billToName || '—';

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f6f8] dark:bg-gray-900">
      {/* ── Top bar: title + back + group actions ─────────────────────────── */}
      <div className="bg-panel px-[22px] pt-3.5 pb-2.5 flex items-center justify-between gap-4 flex-nowrap flex-shrink-0">
        <h1 className="m-0 text-[1.0625rem] font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2.5 tracking-[-0.01em]">
          <span className="text-blue-600 dark:text-blue-400 font-semibold">{group.customerName}</span>
          {isNewBillTo ? (
            <span className="px-2.5 py-[3px] text-[0.6875rem] font-bold uppercase tracking-wider rounded border bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">
              (NEW BILL TO)
            </span>
          ) : (
            <span className="text-[0.78125rem] text-slate-500 dark:text-slate-400 font-normal">· {billToLabel}</span>
          )}
          <span className="text-[0.78125rem] text-slate-400 dark:text-slate-500 font-normal">· {group.laneCount} {group.laneCount === 1 ? 'lane' : 'lanes'}</span>
        </h1>
        <div className="flex gap-2">
          <GhostBtn onClick={goBack}>← Back to Quotes</GhostBtn>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3.5 px-[22px] pt-3.5 pb-8">
        <GroupHeaderCard group={group} />

        {/* ── Direct Quotes — shared TanTable (sort / filter / export / persisted
            layout); edit in place via the drawer. Title + "+ Add Quote" ride in
            the table header. Hidden when empty. ── */}
        {directLanes.length > 0 && (
          // Tracks which quotes you've opened this session (green pill + a ring on
          // the most recently opened) via the Quote # column.
          <ViewedProsProvider>
            <DirectQuotesTable
              lanes={directLanes}
              onOpenLane={handleOpenDirect}
              onAddQuote={() => setCreateOpen(true)}
              showEnded={showEnded}
              onToggleEnded={setShowEnded}
              endedLoading={endedLoading}
            />
          </ViewedProsProvider>
        )}

        {/* ── Warehouse Quotes — route out to the single-quote editor. Hidden
            when empty. ─────────────────────────────────────────────────────── */}
        {warehouseLanes.length > 0 && (
          <section>
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <h2 className="m-0 text-[13px] font-bold text-slate-900 dark:text-slate-100">
                Warehouse Quotes ({warehouseLanes.length})
              </h2>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg dark:bg-slate-800 dark:border-slate-700">
              <LaneTable
                lanes={warehouseLanes}
                onOpenLane={handleOpenWarehouse}
              />
            </div>
          </section>
        )}
      </div>

      {/* The per-lane editor drawer. key={quoteId} remounts it per lane so a
          reused instance never shows the previous lane's state. */}
      <QuoteLaneEditorDrawer
        key={openLane?.quoteId}
        open={!!openLane}
        lane={openLane}
        onClose={() => setOpenLane(null)}
        onChanged={refetchGroup}
      />

      {/* "+ Add Quote" reuses the unified editor drawer in create mode, pre-scoped
          to this group's customer + bill-to. */}
      <QuoteLaneEditorDrawer
        key="new-quote"
        open={createOpen}
        lane={null}
        mode="create"
        initialHeader={addLaneHeader}
        onClose={() => setCreateOpen(false)}
        onChanged={refetchGroup}
      />
    </div>
  );
};

/* ── Shared group header card ──────────────────────────────────────────────
 * The customer/bill-to identity is identical across the group's lanes, so we
 * lazily load the FIRST lane's full detail (getQuote) once and reuse the
 * existing CustomerInfoTab content for the richer shared info (address / CSR /
 * contact). Until that resolves (or if there's no openable lane / it fails) we
 * still show the customer + bill-to from the group object itself. */
const GroupHeaderCard: React.FC<{ group: QuoteGroup }> = ({ group }) => {
  // Memoize so the effect's dependency is a stable object reference (the
  // .find() result would otherwise be recomputed every render and re-fire the
  // fetch). Keyed on group.lanes, which only changes when the group does.
  const firstOpenable = React.useMemo(
    () => group.lanes.find((l) => l.quoteNumber != null),
    [group.lanes],
  );
  const [detail, setDetail] = React.useState<QuoteDetailData | null>(null);
  const [open, setOpen] = React.useState(true);

  React.useEffect(() => {
    if (!firstOpenable || firstOpenable.quoteNumber == null) return;
    let cancelled = false;
    quoteService.getQuote(firstOpenable.quoteNumber, firstOpenable.revisionNumber)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((err: unknown) => {
        // Fall back to the group-object summary below, but don't swallow a
        // persistently-failing fetch silently — log so it's diagnosable.
        console.warn('[GroupedQuoteDetail] header detail fetch failed', err);
      });
    return () => { cancelled = true; };
  }, [firstOpenable]);

  const isNewBillTo = group.billToNumber === 0;

  return (
    <section className="bg-white border border-slate-200 rounded-lg dark:bg-slate-800 dark:border-slate-700">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? 'collapse account details' : 'expand account details'}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 border-b border-slate-200 dark:border-slate-700 text-left"
      >
        <ChevronRightIcon className={`w-4 h-4 text-slate-500 dark:text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
        {/* Customer / Bill To already head the page above — this card just holds
            the account fields, so it's titled "Account Details" (no duplication). */}
        <span className="text-[0.8125rem] font-bold text-slate-900 dark:text-slate-100">Account Details</span>
      </button>

      {open && (
        <div className="px-3.5 py-3">
          {detail ? (
            <>
              {/* Reuse the single-quote Customer Info content, but in the
                  groupHeader variant: the PER-QUOTE bits (V2 Quote#/Lane, Fuel
                  Calculation, Rate Applies, per-quote Volume/Revenue/Margin +
                  the YoY/Revision stat grids) are HIDDEN here — they live in the
                  per-lane editor drawer. Read-only (no edit callbacks passed). */}
              <CustomerInfoTab
                data={detail}
                variant="groupHeader"
                flat
                showIntelligence
                groupAverages={{
                  revenue: group.averages?.revenue ?? null,
                  margin: group.averages?.margin ?? null,
                }}
              />
            </>
          ) : (
            <div className="text-[0.75rem] text-slate-500 dark:text-slate-400">
              <div><span className="text-slate-400 dark:text-slate-500">Customer:</span> <span className="font-semibold text-slate-800 dark:text-slate-200">{group.customerName}</span></div>
              <div className="mt-0.5">
                <span className="text-slate-400 dark:text-slate-500">Bill To:</span>{' '}
                {isNewBillTo
                  ? <span className="font-semibold text-amber-700 dark:text-amber-400">(NEW BILL TO)</span>
                  : <span className="font-semibold text-slate-800 dark:text-slate-200">{group.billToName || '—'}</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

/* ── Small chrome helpers (mirror QuoteDetailPage's shells) ───────────────── */

const Shell: React.FC<{ goBack: () => void; title: string; children: React.ReactNode }> = ({ goBack, title, children }) => (
  <div className="min-h-screen flex flex-col bg-[#f5f6f8] dark:bg-gray-900">
    <div className="bg-panel px-[22px] pt-3.5 pb-2.5 flex items-center justify-between gap-4 flex-shrink-0">
      <h1 className="m-0 text-[1.0625rem] font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
      <div className="flex gap-2">
        <GhostBtn onClick={goBack}>← Back to Quotes</GhostBtn>
      </div>
    </div>
    <div className="flex-1 flex flex-col">{children}</div>
  </div>
);

const CenteredCard: React.FC<{
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
}> = ({ title, body, action }) => (
  <div className="flex-1 flex items-center justify-center">
    <div className="bg-panel border border-slate-200 dark:border-gray-700 rounded-lg px-6 py-8 max-w-md text-center">
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">{title}</div>
      <div className="text-[0.78125rem] text-slate-600 dark:text-slate-400">{body}</div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-4 px-3.5 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          {action.label}
        </button>
      )}
    </div>
  </div>
);

const GhostBtn: React.FC<{ children: React.ReactNode; onClick?: () => void }> = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-3.5 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600"
  >
    {children}
  </button>
);

export default GroupedQuoteDetailPage;
